import { Button } from '@/components/ui/button';
import { SEOProps } from '@/components/ui/SEO';
import { UnderlinedText } from '@/components/ui/underline-text';
import { basePath, withBasePath } from '@/utils/basePath';
import { FAQ_LIST, STATUTS_LIST, SUMMARY_ITEMS } from '@/utils/data/facturationZlawyerContent';
import { useGSAP } from '@gsap/react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import Image from 'next/image';
import { type ReactNode, useMemo, useState } from 'react';

gsap.registerPlugin(ScrollTrigger);

const FACTURATION_ELECTRONIQUE_ZLAWYER_SEO: SEOProps = {
  title: 'Facturation électronique avocat : solution zLawyer conforme 2026',
  description:
    'Découvrez comment zLawyer accompagne les cabinets d’avocats dans la transition vers la facturation électronique obligatoire 2026 avec une solution conforme, sécurisée et sans surcoût.',
  keywords:
    'facturation électronique avocat, logiciel avocat, zLawyer, conformité 2026, Factur-X, plateforme agréée',
  url: `https://www.zlawyer.fr${basePath}/facturation-electronique-et-zlawyer`,
};

function SummaryLink({ active, href, label }: { active: boolean; href: string; label: string }) {
  return (
    <a
      href={href}
      className={`hover:text-orange block py-1 text-sm transition-colors duration-200 ${
        active ? 'text-orange font-bold' : ''
      }`}
    >
      {label}
    </a>
  );
}

function Bullet({ children, icon = '•' }: { children: ReactNode; icon?: ReactNode }) {
  return (
    <li className="flex items-start">
      <span className="text-orange mt-1 mr-3">{icon}</span>
      <div>{children}</div>
    </li>
  );
}

function AccordionItem({
  isOpen,
  onToggle,
  question,
  answer,
}: {
  isOpen: boolean;
  onToggle: () => void;
  question: string;
  answer: ReactNode;
}) {
  return (
    <div className="group overflow-hidden rounded-(--radius-current) border border-gray-100 bg-white shadow-sm transition-all duration-300 hover:shadow-md">
      <button
        className="flex w-full items-center justify-between px-6 py-5 text-left transition-all duration-200 hover:bg-gray-50 focus:outline-none"
        type="button"
        onClick={onToggle}
      >
        <span className="pr-4 font-semibold text-gray-800">{question}</span>
        <div className="shrink-0">
          <svg
            fill="none"
            height="20"
            viewBox="0 0 24 24"
            width="20"
            className={`transform transition-all duration-500 ease-in-out ${
              isOpen ? 'text-orange scale-110 rotate-180' : 'group-hover:text-orange text-gray-400'
            }`}
          >
            <path
              d="M6 9l6 6 6-6"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2.5"
            />
          </svg>
        </div>
      </button>
      <div
        className={`grid transition-all duration-500 ease-in-out ${
          isOpen ? 'grid-rows-[1fr] opacity-100' : 'grid-rows-[0fr] opacity-0'
        }`}
      >
        <div className="overflow-hidden">
          <div className="border-t border-gray-100 bg-linear-to-b from-gray-50/50 to-white px-6 pt-4 pb-6">
            {answer}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function FacturationElectroniqueEtZlawyer() {
  const [activeSection, setActiveSection] = useState('partenariat');
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [openStatuts, setOpenStatuts] = useState<number | null>(null);
  const summaryItems = useMemo(() => SUMMARY_ITEMS, []);
  const sections = Array.from(document.querySelectorAll<HTMLElement>('section[id]'));

  useGSAP(() => {
    if (!sections.length) return;

    const triggers = sections.map((section) =>
      ScrollTrigger.create({
        trigger: section,
        start: 'top 125px',
        end: 'bottom center',
        onEnter: () => setActiveSection(section.id),
        onEnterBack: () => setActiveSection(section.id),
      }),
    );

    ScrollTrigger.refresh();

    return () => {
      triggers.forEach((trigger) => trigger.kill());
    };
  }, []);

  return (
    <div className="max-width-blue-section relative">
      <div className="grid w-full grid-cols-1 items-center gap-8 lg:grid-cols-2">
        <div className="flex flex-col gap-4">
          <h1 className="text-left text-3xl">
            zLawyer <UnderlinedText>anticipe la réforme</UnderlinedText> de la facturation
            électronique et <UnderlinedText>structure la transition</UnderlinedText> des
            cabinets d&apos;avocats
          </h1>

          <p>
            La réforme de la <span className="font-bold">facturation électronique</span>, applicable
            à compter de 2026, marque une évolution profonde des obligations applicables aux
            cabinets d&apos;avocats.
          </p>
          <p>
            Au-delà d&apos;une contrainte réglementaire, elle constitue une transformation
            structurelle des processus de facturation et de transmission des données.
          </p>
          <p>
            Fidèle à sa mission : proposer un{' '}
            <span className="font-bold">
              logiciel métier complet et sécurisé dédié aux professionnels du droit
            </span>
            , zLawyer anticipe cette évolution en intégrant la facturation électronique{' '}
            <span className="font-bold">sans surcoût</span> au cœur de sa solution.
          </p>

          <Button className="mt-4 w-fit" href="/contact" variant="transparent">
            Je souhaite être contacté
          </Button>
        </div>
        <Image
          alt="illustration du logiciel"
          className="w-full rounded-(--radius-current)"
          height={600}
          src={withBasePath('/images/features/features10.webp')}
          width={900}
        />
      </div>

      <div className="mt-32 flex w-full gap-8">
        <aside className="hidden lg:block">
          <div className="sticky top-32 left-0 h-fit w-64 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <h3 className="text-orange mb-4 font-semibold">Sommaire</h3>
            <ul className="space-y-3">
              {summaryItems.map((item) => (
                <li key={item.href}>
                  <SummaryLink
                    active={activeSection === item.href.slice(1)}
                    href={item.href}
                    label={item.label}
                  />
                </li>
              ))}
            </ul>
          </div>
        </aside>

        <div className="w-full">
          <section className="scroll-mt-32 pb-16" id="partenariat">
            <h2 className="mb-6 text-2xl! font-bold">
              Facturation électronique pour les avocats :{' '}
              <UnderlinedText>zLawyer</UnderlinedText> s’impose comme partenaire stratégique
              des cabinets
            </h2>
            <p className="leading-relaxed">
              La réforme de la{' '}
              <span className="font-bold">
                facturation électronique obligatoire à compter de 2026
              </span>{' '}
              transforme durablement l’environnement réglementaire des cabinets d’avocats.
            </p>
            <p className="mt-4">
              L’émission et la réception de factures électroniques, la transmission des données
              selon des formats structurés tels que <span className="font-bold">Factur-X</span>, et
              l’adaptation des processus internes deviennent des enjeux centraux pour la gestion des
              cabinets.
            </p>
            <p className="mt-4">
              Face à cette évolution, zLawyer ne se contente pas d’adapter sa solution :
              <br />
              <span className="font-bold">
                zLawyer structure et anticipe la transition des cabinets d’avocats vers la
                facturation électronique
              </span>
              .
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="continuite">
            <h2 className="mb-6 text-2xl! font-bold">
              zLawyer, logiciel avocat prêt pour la réforme 2026
            </h2>
            <p>
              Conçu exclusivement pour les professionnels du droit,{' '}
              <span className="font-bold">
                zLawyer est un logiciel de gestion pour cabinet d’avocats
              </span>{' '}
              intégrant :
            </p>
            <ul className="mt-2 mb-5 ml-4">
              <Bullet>La gestion des dossiers,</Bullet>
              <Bullet>Le suivi des temps et des honoraires,</Bullet>
              <Bullet>La facturation,</Bullet>
              <Bullet>
                Et désormais la{' '}
                <span className="font-bold">
                  facturation électronique conforme aux exigences 2026
                </span>
                .
              </Bullet>
            </ul>
            <p>
              La facturation électronique est intégrée nativement{' '}
              <span className="font-bold">et sans surcoût</span> dans l’environnement zLawyer afin
              de garantir :
            </p>
            <ul className="mt-4 ml-4 space-y-2">
              <Bullet>La continuité organisationnelle du cabinet,</Bullet>
              <Bullet>L’absence de double outil,</Bullet>
              <Bullet>La centralisation des flux juridiques et financiers.</Bullet>
            </ul>
          </section>

          <section className="scroll-mt-32 pb-16" id="conformite">
            <h2 className="mb-6 text-2xl! font-bold">
              Un partenariat stratégique avec Pennylane au service des cabinets d’avocats
            </h2>
            <p>
              Pour accompagner cette transformation réglementaire, zLawyer a noué un partenariat
              stratégique avec{' '}
              <a
                className="text-orange underline"
                href="https://www.pennylane.com/fr/logiciel-facturation-electronique"
                rel="noreferrer"
                target="_blank"
              >
                Pennylane
              </a>
              , plateforme agréée de référence, gage de conformité et de fiabilité.
            </p>
            <p className="my-2">Ce partenariat permet d’intégrer directement dans zLawyer :</p>
            <ul className="mt-4 mb-6 ml-4 space-y-2">
              <Bullet>
                L’émission et la réception de factures électroniques au format{' '}
                <span className="font-bold">Factur-X</span>,
              </Bullet>
              <Bullet>
                La transmission des données selon les modalités prévues par la réforme,
              </Bullet>
              <Bullet>Un suivi structuré du cycle de vie des factures,</Bullet>
              <Bullet>
                Une infrastructure conçue pour répondre aux standards de sécurité et d’évolutivité
                attendus.
              </Bullet>
            </ul>
            <p>
              La solution est conçue pour évoluer en fonction des adaptations du cadre réglementaire
              applicable aux entreprises.
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="approche-metier">
            <h2 className="mb-6 text-2xl! font-bold">
              Facturation électronique cabinet d’avocats : une approche métier, pas générique
            </h2>
            <p>
              <span className="font-bold">
                La réforme 2026 ne concerne pas seulement la technologie.
              </span>{' '}
              Elle impacte l’organisation interne, la gestion financière et la relation client.
            </p>
            <p className="mt-4">
              <span className="font-bold">zLawyer adopte une approche structurée</span> :
            </p>
            <ul className="mt-4 mb-6 ml-4 space-y-2">
              <Bullet icon="✓">Intégration native dans notre logiciel avocat,</Bullet>
              <Bullet icon="✓">Gestion centralisée des dossiers, temps et factures,</Bullet>
              <Bullet icon="✓">Sécurisation des échanges de données*,</Bullet>
              <Bullet icon="✓">Accompagnement dans la mise en œuvre opérationnelle.</Bullet>
            </ul>
            <p>
              La fonctionnalité de facturation électronique est incluse dans l’abonnement et donc
              <span className="font-bold"> sans surcoût.</span>
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="performance-cabinet">
            <h2 className="mb-6 text-2xl! font-bold">
              Anticiper la conformité, renforcer la performance du cabinet
            </h2>
            <p className="my-2">
              <span className="font-bold">
                Choisir zLawyer pour la facturation électronique, c’est :
              </span>
            </p>
            <ul className="mt-4 mb-6 ml-4 space-y-2">
              <Bullet>Anticiper les obligations réglementaires,</Bullet>
              <Bullet>Sécuriser les processus de facturation*,</Bullet>
              <Bullet>Maintenir la fluidité organisationnelle du cabinet,</Bullet>
              <Bullet>S’appuyer sur un partenaire technologique engagé dans la durée.</Bullet>
            </ul>
            <p>
              Au-delà de la conformité, zLawyer transforme la réforme en opportunité d’optimiser et
              de structurer durablement la gestion de votre cabinet.
            </p>

            <p className="mt-4 text-sm italic">
              *Vos données sont protégées par une infrastructure certifiée ISO 27001, déployée sur
              serveur SecNumCloud.
            </p>
          </section>

          <section className="scroll-mt-32 pb-16" id="preparation">
            <h2 className="mb-6 text-2xl! font-bold">
              Préparez dès maintenant votre cabinet à la facturation électronique obligatoire
            </h2>
            <p>Chaque cabinet présente des spécificités, nos équipes vous accompagnent pour :</p>
            <ul className="mt-4 ml-4 space-y-2">
              <Bullet icon="✓">Analyser votre organisation actuelle,</Bullet>
              <Bullet icon="✓">
                Anticiper l’impact de la réforme de la facturation électronique,
              </Bullet>
              <Bullet icon="✓">Structurer la mise en œuvre au sein de votre cabinet.</Bullet>
            </ul>
          </section>

          <hr className="mb-12 border-gray-200" />

          <section className="mb-16">
            <div className="px-8 py-4 text-center">
              <h2 className="mb-4 text-2xl! font-bold">
                <UnderlinedText>Besoin</UnderlinedText> d&apos;en savoir plus ?
              </h2>
              <p className="mb-6">
                Contactez-nous dès maintenant pour une démonstration personnalisée de zLawyer
              </p>
              <Button href="/contact" variant="transparent">
                Je souhaite être contacté
              </Button>
            </div>
          </section>

          <hr className="my-12 border-gray-200" />

          <section className="mb-16 scroll-mt-32" id="faq">
            <h2 className="mb-8 text-2xl! font-bold">
              <UnderlinedText>FAQ</UnderlinedText> – Questions fréquentes
            </h2>
            <p className="mb-8 text-gray-600">
              Voici le Forum des Questions (FAQ) sur la facturation électronique
            </p>

            <div className="space-y-3">
              {FAQ_LIST.map((item, idx) => (
                <AccordionItem
                  key={item.q}
                  answer={<div dangerouslySetInnerHTML={{ __html: item.a }} />}
                  isOpen={openFaq === idx}
                  question={item.q}
                  onToggle={() => setOpenFaq(openFaq === idx ? null : idx)}
                />
              ))}
            </div>
          </section>

          <hr className="my-12 border-gray-200" />

          <section className="mb-16 scroll-mt-32" id="statuts-factures">
            <h2 className="mb-8 text-2xl! font-bold text-gray-800">
              Les principaux statuts d&apos;une facture qui seront mis en place
            </h2>
            <p className="mb-8">
              Une facture peut passer par plusieurs statuts normalisés tout au long de son cycle :
            </p>

            <div className="space-y-3">
              {STATUTS_LIST.map((item, idx) => (
                <AccordionItem
                  key={item.q}
                  answer={<div dangerouslySetInnerHTML={{ __html: item.a }} />}
                  isOpen={openStatuts === idx}
                  question={item.q}
                  onToggle={() => setOpenStatuts(openStatuts === idx ? null : idx)}
                />
              ))}
            </div>

            <div className="mt-8 rounded-lg border-l-4 border-blue-400 bg-blue-50 p-6">
              <p className="font-medium text-blue-800">
                📊 Chaque changement de statut est remonté en temps réel, offrant une visibilité
                complète sur l&apos;état d&apos;avancement des factures.
              </p>
            </div>
          </section>

          <section className="mb-16">
            <div className="p-8 text-center">
              <h2 className="mb-4 text-2xl! font-bold">
                <UnderlinedText>Besoin</UnderlinedText> d&apos;en savoir plus ?
              </h2>
              <p className="mb-6">
                Contactez-nous dès maintenant pour une démonstration personnalisée de zLawyer
              </p>
              <Button href="/contact" variant="transparent">
                Je souhaite être contacté
              </Button>
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}

FacturationElectroniqueEtZlawyer.seo = FACTURATION_ELECTRONIQUE_ZLAWYER_SEO;
